import React, { useState, useEffect } from 'react';
import axios from '../../utils/axiosConfig';
import { formatDate } from '../../utils/dates';
import { t } from '../../utils/translations';
import KebabMenu, { MENU_ICONS, useKebabMenu } from '../../components/KebabMenu';
import '../Products.css';

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useKebabMenu();
  const [formData, setFormData] = useState({
    name: '',
    alias_name: '',
    email: '',
    phone: '',
    address: '',
    status: 'active',
    gst_enabled: false,
    gst_number: '',
    field_orders_enabled: false
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get('/api/clients');
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      alert(t('Failed to fetch clients'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(`/api/clients/${editingClient.id}`, formData);
      } else {
        await axios.post('/api/clients', formData);
      }

      setShowModal(false);
      setEditingClient(null);
      resetForm();
      fetchClients();
    } catch (error) {
      alert(error.response?.data?.message || t('Failed to save client'));
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      alias_name: client.alias_name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      status: client.status || 'active',
      gst_enabled: Boolean(Number(client.gst_enabled)),
      gst_number: client.gst_number || '',
      field_orders_enabled: Boolean(Number(client.field_orders_enabled))
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Are you sure you want to delete this client? This will delete all associated data.'))) {
      return;
    }

    try {
      await axios.delete(`/api/clients/${id}`);
      fetchClients();
    } catch (error) {
      alert(t('Failed to delete client'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      alias_name: '',
      email: '',
      phone: '',
      address: '',
      status: 'active',
      gst_enabled: false,
      gst_number: '',
      field_orders_enabled: false
    });
  };

  const openModal = () => {
    resetForm();
    setEditingClient(null);
    setShowModal(true);
  };

  if (loading) {
    return <div className="page-container"><div className="container">{t('Loading...')}</div></div>;
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1>{t('Client Management')}</h1>
          <button onClick={openModal} className="btn btn-primary">
            {t('Add Client')}
          </button>
        </div>
        <p style={{ color: '#666', marginTop: -10, marginBottom: 16, fontSize: 14 }}>
          Same shop with two departments? Create <strong>two clients</strong> with the same Client Name and GSTIN,
          and different Alias names (e.g. General Store / Cloth Store). Assign the same user to both — at login they pick the alias.
        </p>

        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>{t('Name')}</th>
                <th>Alias</th>
                <th>{t('Email')}</th>
                <th>{t('Phone')}</th>
                <th>{t('Address')}</th>
                <th>{t('Status')}</th>
                <th>GST</th>
                <th>Field orders</th>
                <th>GSTIN</th>
                <th>{t('Created')}</th>
                <th>{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 ? (
                <tr>
                  <td colSpan="11" style={{ textAlign: 'center' }}>{t('No clients found')}</td>
                </tr>
              ) : (
                clients.map(client => (
                  <tr key={client.id}>
                    <td>{client.name}</td>
                    <td>{client.alias_name || '—'}</td>
                    <td>{client.email || '-'}</td>
                    <td>{client.phone || '-'}</td>
                    <td>{client.address || '-'}</td>
                    <td>
                      <span className={`status-badge status-${client.status}`}>
                        {client.status}
                      </span>
                    </td>
                    <td>{Number(client.gst_enabled) ? 'Enabled' : 'Disabled'}</td>
                    <td>{Number(client.field_orders_enabled) ? 'Enabled' : 'Disabled'}</td>
                    <td>{client.gst_number || '-'}</td>
                    <td>{formatDate(client.created_at)}</td>
                    <td>
                      <KebabMenu
                        menuId={`client-${client.id}`}
                        openId={openActionMenu}
                        setOpenId={setOpenActionMenu}
                        label={t('Actions')}
                        items={[
                          { label: t('Edit'), icon: MENU_ICONS.edit, onClick: () => handleEdit(client) },
                          { label: t('Delete'), icon: MENU_ICONS.trash, danger: true, onClick: () => handleDelete(client.id) }
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>{editingClient ? t('Edit Client') : t('Add Client')}</h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('Client Name')} * <small style={{ color: '#666', fontWeight: 400 }}>(shown on invoice)</small></label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Alias name <small style={{ color: '#666', fontWeight: 400 }}>(shown when selecting client at login)</small></label>
                  <input
                    type="text"
                    value={formData.alias_name}
                    onChange={(e) => setFormData({ ...formData, alias_name: e.target.value })}
                    placeholder="Short / easy name for staff"
                  />
                </div>
                <div className="form-group">
                  <label>{t('Email')}</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('Phone')}</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('Address')}</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label>{t('Status')}</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="active">{t('Active')}</option>
                    <option value="inactive">{t('Inactive')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(formData.gst_enabled)}
                      onChange={(e) => setFormData({ ...formData, gst_enabled: e.target.checked })}
                    />
                    Enable GST for this client
                  </label>
                  <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                    When enabled, products can use GST % and invoices will calculate GST.
                  </small>
                </div>
                <div className="form-group">
                  <label>GST Number (GSTIN)</label>
                  <input
                    type="text"
                    value={formData.gst_number}
                    onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                    placeholder="e.g. 22AAAAA0000A1Z5"
                    maxLength={20}
                  />
                  <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                    Shown on the invoice header when this client creates a GST invoice.
                  </small>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(formData.field_orders_enabled)}
                      onChange={(e) => setFormData({ ...formData, field_orders_enabled: e.target.checked })}
                    />
                    Enable field orders for this client
                  </label>
                  <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                    Salesmen can take market orders on the mobile app. The office approves, dispatches, and invoices them here.
                  </small>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingClient ? t('Update') : t('Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientManagement;
